'use client';

import { useCallback, useRef } from 'react';
import type { ApplicationDocumentationInitialAction } from './applicationDocumentation.constants';

export type InitialDocumentationActionResolver = {
  resolveAdvance: () => void;
  resolveCancel: () => void;
  beginAdvance: () => void;
  endAdvance: () => void;
};

export const useInitialDocumentationAction = ({
  initialDocumentationAction,
  onAdvance,
  onCancel,
}: {
  initialDocumentationAction?: ApplicationDocumentationInitialAction;
  onAdvance?: () => void;
  onCancel?: () => void;
}): InitialDocumentationActionResolver => {
  const hasResolvedRef = useRef(false);
  const isAdvancingRef = useRef(false);

  const resolveAdvance = useCallback(() => {
    if (!initialDocumentationAction || hasResolvedRef.current) {
      return;
    }

    hasResolvedRef.current = true;
    onAdvance?.();
  }, [initialDocumentationAction, onAdvance]);

  const resolveCancel = useCallback(() => {
    if (
      !initialDocumentationAction ||
      hasResolvedRef.current ||
      isAdvancingRef.current
    ) {
      return;
    }

    hasResolvedRef.current = true;
    onCancel?.();
  }, [initialDocumentationAction, onCancel]);

  const beginAdvance = useCallback(() => {
    isAdvancingRef.current = true;
  }, []);

  const endAdvance = useCallback(() => {
    isAdvancingRef.current = false;
  }, []);

  return { resolveAdvance, resolveCancel, beginAdvance, endAdvance };
};
